<template lang="pug">
.w-input( :class=`
  [
    {
      focus: isFocused,
      filled,
      required,
    },
    ...inputFeedback.classes
  ]` )
  .input-wrapper
    slot( name='label' v-bind='{ label, inputType, value, listeners }' )
      label
        .label {{ label }}
        slot( v-bind='{ label, inputType, value, listeners }' )
          input(
            :type='inputType'
            :value='value'
            ref='input'
            v-on="listeners"
          )
    .input-actions
      transition-group( name='fade' mode='out-in' )
        button.input-action( v-for='action in inputActions' )
  .bar( :class='barClass' )
  transition( name='fade' mode='out-in' appear )
    .feedback(
      v-if='feedback || isValid === false'
      :key='inputFeedback.type'
      :class='inputFeedback.classes' )
      | {{ inputFeedback.message }}
</template>

<script lang="js">
// import { Logger } from '@/lib/logger'
import isArray from 'lodash-es/isArray'
import isObject from 'lodash-es/isObject'
import isFunction from 'lodash-es/isFunction'

export const mixin = {
  props: {
    value: [ Number, String, Object, Array, File, Date ],
    required: Boolean,
    disabled: Boolean,
  },
  methods: {
    input( value ) {
      this.$emit( 'input', value )
    },
    change( value ) {
      this.$emit( 'change', value )
    },
    focus() {
      const input = this.$refs.input
      if ( input ) {
        (isArray( input ) ? input[0] : input).focus()
      }
    },
    blur() {
      const input = this.$refs.input
      if ( input ) {
        (isArray( input ) ? input[0] : input).blur()
      }
    },
    select() {
      const input = this.$refs.input
      if ( input ) {
        (isArray( input ) ? input[0] : input).select()
      }
    },
  }
}

export default {
  name: 'InputWidget',
  mixins: [],
  components: {},
  props: {
    type: {
      type: String,
      default: 'text'
    },
    value: {
      type: [ Number, String, Object, Array, File, Date ],
      default: ''
    },
    label: {
      type: String,
      default: 'input'
    },
    required: Boolean,
    feedback: Object,
    actions: {
      type: Array,
      default: () => []
    },
    validate: [ Object, Array, Function ]
  },
  data() {
    return {
      inputType: '',
      passwordShown: false,
      isFocused: false,
      validating: false,
      errors: null
    }
  },
  created() {
    this.inputType = this.type
  },
  mounted() {},
  methods: {
    async doValidate( value ) {
      if ( !this.validate && !this.required ) return
      this.validating = true
      this.errors = null
      const validation = await Promise.all(
        this.validations.map( async validator => {
          const res = await validator.validate( value )
          if ( res ) {
            return {
              type: validator.name,
              error: res
            }
          }
        }))
      this.validating = false
      this.errors = validation.filter( x => x )
    },
    focus() {
      const input = this.$refs.input
      if ( input ) {
        (isArray( input ) ? input[0] : input).focus()
      }
    },
    blur() {
      const input = this.$refs.input
      if ( input ) {
        (isArray( input ) ? input[0] : input).blur()
      }
    },
    select() {
      const input = this.$refs.input
      if ( input ) {
        (isArray( input ) ? input[0] : input).select()
      }
    },
  },
  computed: {
    listeners() {
      return {
        ...this.$listeners,
        focus: () => {
          this.isFocused = true
          this.$emit( 'focus' )
        },
        blur: () => {
          this.isFocused = false
          this.doValidate( this.value )
          this.$emit( 'blur' )
        },
        input: $event => {
          const value = $event.target ? $event.target.value : $event
          if ( this.isValid === false ) {
            this.$nextTick(() => this.doValidate( value ))
            // TODO: debounce
          }
          this.$emit( 'input', value )
        },
      }
    },
    isPassword() {
      return this.type === 'password'
    },
    inputFeedback() {
      if ( this.feedback ) {
        const feedback = Object.assign({
          type: '',
          classes: this.feedback && this.feedback.type,
          message: ''
        }, this.feedback )
        return feedback
      }
      if ( this.isValid === false ) {
        return {
          type: 'error',
          classes: [ 'error', 'invalid' ],
          message: this.errors[0].error
        }
      }
      return {
        type: null,
        message: null
      }
    },
    barClass() {
      return ''
    },
    inputActions() {
      const actions = this.isPassword && this.allowShowPassword
            ? [{
              name: 'show-password',
              icon: this.passwordShown ? 'show-password' : 'hide-password',
              classes: ''
            }, ...this.actions ]
            : this.actions
      return actions
    },
    filled() {
      return !!( isArray( this.value )
                 ? this.value.length
                 : isObject( this.value )
                 ? Object.keys( this.value ).length
                 : this.value )
    },
    validations() {
      let valid = []
      if ( isArray( this.validate )) {
        valid = this.validate
      } else if ( isFunction( this.validate )) {
        valid = [{
          name: 'function',
          validate: this.validate
        }]
      } else if ( this.validate ) {
        valid = [ this.validate ]
        // TODO: if Object.keys( this.validate ).length === 1 &&
        // Objec.values( this.validate )[0].name
      }
      const validations = this.required
            ? [{
              name: 'required',
              validate: function ( value ) {
                if ( !value ) {
                  return 'this field is required'
                }
              }
            }, ...valid ]
            : valid.length ? valid : null
      return validations
    },
    isValid () {
      if ( !this.errors ) return null
      return !this.errors.length
    }
  }
}
</script>

<style lang="scss">
.w-input {
  position: relative;
  width: var(--w-input, 100%);
  margin-bottom: var(--m-input, 1em);
  padding-bottom: var(--p-input, 1em);
  font-size: var(--fs-input, var(--s-lg));
  opacity: 0.8;
  transition: width,height,opacity 250ms;
  .input-wrapper {
    display: flex;
    flex-wrap: nowrap;
    border-bottom: 2px solid var(--c-input-border, rgba(255,255,255,0.25));
    transition: border-bottom-color 250ms;
    > label {
      flex: 1 1 100%;
      display: block;
      position: relative;
      width: 100%;
      padding-top: 0.75em;
      margin: 0;
      .label {
        padding: 0;
        transition: 150ms all;
        font-size: 1em;
        position: absolute;
        top: 0.75em;
        left: 0;
        line-height: inherit;
        color: var(--c-text, black);
      }
    }
    input, .input {
      background: transparent;
      color: var(--c-text-input, var(--c-text, white));
      border: var(--b-input, 0 none);
      border-radius: var(--br-input, 0);
      width: 100%;
      height: var(--h-input, 2rem);
      &:focus {
        outline: 0 none !important;
      }
    }
  }
  .bar {
    transition: 250ms all;
    height: 2px;
    width: 0%;
    /* background: rgba(255,255,255,0.25); */
    background: rgba(0,120,255, 1);
    margin-top: -2px;
  }
  &:hover, &:focus, &.focus {
    opacity: 1;
    .input-wrapper {
    border-bottom-color: var(--c-input-border-active, rgba(255,255,255,0.4));
    }
  }
  &.focus {
    .input-wrapper {
      > label {
        .label {
          font-size: 0.65em;
          top: 0;
        }
      }
    }
    .bar {
      background: rgba(0,120,255, 1);
      width: 100%;
    }
  }
  &.filled {
    .input-wrapper {
      > label {
        .label {
          font-size: 0.65em;
          top: 0;
        }
      }
    }
  }
  &.required {
    .input-wrapper {
      > label {
        .label {
          &::after {
            content: " *"
          }
        }
      }
    }
  }
  .feedback {
    text-align: left;
    font-size: var(--s-3q);
    position: absolute;
    left: var(--pos-feedback-left, 0);
    right: var(--pos-feedback-right, unset);
    bottom: var(--pos-feedback-bottom, 0);
    &.warning {
      color: yellow
    }
    &.error {
      color: red
    }
    &.success {
      color: lime
    }
  }
  &.warning, &.error {
    .input-wrapper {
      > label {
        .label {
          &::after {
            color: yellow;
          }
        }
      }
    }
    .bar {
      width: 100%;
    }
  }
  &.warning {
    .bar {
      background: yellow
    }
  }
  &.error {
    .input-wrapper {
      > label {
        .label {
          &::after {
            color: red;
          }
        }
      }
    }
    .bar {
      background: red
    }
  }
  &.success {
    .input-wrapper {
      > label {
        .label {
          &::after {
            color: lime;
          }
        }
      }
      border-color: green;
    }
    .bar {
      background: lime;
    }
  }
  &.disabled {
    opacity: 0.5;
    &, label, .label {
      text-shadow: 0 1px 1px white;
      color: transparent;
      background-clip: text;
      background-color: rgba(0,0,0,0.5);
    }
  }
}
</style>
